import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { Anchor } from 'shared/map/domain/anchor';
import type { Coordinate } from 'shared/map/domain/coordinate';
import { createPreviewRectangle, geoJsonRectangle } from 'shared/map/domain/rectangle';
import { bearing, destination, distance, midpoint, point, type Units } from '@turf/turf';
import { getShapeId, setShapeId, type ShapeGeoJson } from 'shared/shapes/domain/shapeGeoJson';

export interface EditShape {
  feature: GeoJSON.Feature;
  shapeId: string;
  addToSelection?: boolean;
}

export interface CreateShape {
  coordinate: Coordinate;
  width?: number;
  length?: number;
}

export interface CopyShape {
  feature: GeoJSON.Feature;
}

const turfOptions: { units: Units } = { units: 'meters' };

export const usePreviewRectangleStore = defineStore('previewRectangle', () => {
  const operation = ref<'create' | 'edit' | 'none'>('none');
  const defaultAnchor = Anchor.Center;
  const defaultGeofence = 0;
  const defaultHeading = 0;
  const defaultLength = 50;
  const defaultWidth = 10;

  const feature = ref<GeoJSON.Feature>();
  const editedShapes = ref<Map<string, GeoJSON.Feature>>(new Map());
  const originalShapeProperties = ref<
    Map<string, { coordinate: [number, number]; heading: number; width: number; length: number }>
  >(new Map());

  const anchor = ref<Anchor>();
  const longitude = ref<number>();
  const latitude = ref<number>();
  const heading = ref<number>();
  const width = ref<number>();
  const length = ref<number>();

  const geofenceTop = ref<number>();
  const geofenceBottom = ref<number>();
  const geofenceLeft = ref<number>();
  const geofenceRight = ref<number>();

  const editShapeId = ref<string | undefined>(undefined);

  const multiEditMode = computed(() => editedShapes.value.size > 1);

  const coordinate = computed(() => {
    if (longitude.value === undefined) return;
    if (latitude.value === undefined) return;
    return {
      longitude: longitude.value,
      latitude: latitude.value,
    };
  });

  const rotationCoordinate = computed(() => {
    if (longitude.value === undefined) return;
    if (latitude.value === undefined) return;
    const r = heading.value;
    if (r === undefined) return;

    if (!multiEditMode.value) {
      // Single shape mode - use original logic
      const w = width.value;
      if (w === undefined || w <= 0) return;
      const anchor = point([longitude.value, latitude.value]);
      return destination(anchor, w + 5, r + 90, { units: 'meters' });
    }

    // Multi-edit mode - find the furthest point from center in all shapes
    const allShapes = allGeoJsonShapes.value;
    if (allShapes.length === 0) return;

    // Calculate center of all shapes
    const centerLon = longitude.value;
    const centerLat = latitude.value;
    const centerPoint = point([centerLon, centerLat]);

    // Find maximum distance from center
    let maxDistance = 0;
    for (const shape of allShapes) {
      const geometry = shape.shape.geometry;
      if (geometry.type === 'Polygon') {
        for (const coord of geometry.coordinates[0]!) {
          const dist = distance(centerPoint, point(coord), { units: 'meters' });
          if (dist > maxDistance) {
            maxDistance = dist;
          }
        }
      }
    }

    // Place rotation handle outside the furthest point
    return destination(centerPoint, maxDistance + 10, r + 90, { units: 'meters' });
  });

  const rectangle = computed(() => {
    if (!feature.value) return;
    return geoJsonRectangle(feature.value);
  });

  const oldAnchor = computed(() => {
    return rectangle.value?.anchor;
  });

  const anchorChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return anchor.value !== oldAnchor.value;
  });

  const oldLongitude = computed(() => {
    const properties = calculateProperties(feature.value);
    return properties?.coordinate[0];
  });

  const longitudeChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return longitude.value !== oldLongitude.value;
  });

  const oldLatitude = computed(() => {
    const properties = calculateProperties(feature.value);
    return properties?.coordinate[1];
  });

  const latitudeChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return latitude.value !== oldLatitude.value;
  });

  const oldHeading = computed(() => {
    const properties = calculateProperties(feature.value);
    return properties?.heading;
  });

  const headingChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return heading.value !== oldHeading.value;
  });

  const oldWidth = computed(() => {
    const properties = calculateProperties(feature.value);
    return properties?.width;
  });

  const widthChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return width.value !== oldWidth.value;
  });

  const oldLength = computed(() => {
    const properties = calculateProperties(feature.value);
    return properties?.length;
  });

  const lengthChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return length.value !== oldLength.value;
  });

  const oldGeofenceTop = computed(() => {
    return rectangle.value?.geofenceTop;
  });

  const geofenceTopChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return geofenceTop.value !== oldGeofenceTop.value;
  });

  const oldGeofenceBottom = computed(() => {
    return rectangle.value?.geofenceBottom;
  });

  const geofenceBottomChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return geofenceBottom.value !== oldGeofenceBottom.value;
  });

  const oldGeofenceLeft = computed(() => {
    return rectangle.value?.geofenceLeft;
  });

  const geofenceLeftChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return geofenceLeft.value !== oldGeofenceLeft.value;
  });

  const oldGeofenceRight = computed(() => {
    return rectangle.value?.geofenceRight;
  });

  const geofenceRightChanged = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return false;
    return geofenceRight.value !== oldGeofenceRight.value;
  });

  const changed = computed(() => {
    if (operation.value === 'create') return true;
    return (
      anchorChanged.value ||
      longitudeChanged.value ||
      latitudeChanged.value ||
      headingChanged.value ||
      widthChanged.value ||
      lengthChanged.value ||
      geofenceTopChanged.value ||
      geofenceBottomChanged.value ||
      geofenceLeftChanged.value ||
      geofenceRightChanged.value
    );
  });

  const geoJson = computed<ShapeGeoJson | undefined>(() => {
    if (operation.value === 'none') return;
    if (anchor.value === undefined) return;
    if (longitude.value === undefined) return;
    if (latitude.value === undefined) return;
    if (heading.value === undefined) return;
    if (width.value === undefined) return;
    if (length.value === undefined) return;
    if (geofenceBottom.value === undefined) return;
    if (geofenceLeft.value === undefined) return;
    if (geofenceRight.value === undefined) return;
    if (geofenceTop.value === undefined) return;

    const result = createPreviewRectangle({
      anchor: anchor.value,
      coordinate: {
        latitude: latitude.value,
        longitude: longitude.value,
      },
      heading: heading.value,
      width: width.value,
      length: length.value,
      geofence: {
        top: geofenceTop.value,
        bottom: geofenceBottom.value,
        left: geofenceLeft.value,
        right: geofenceRight.value,
      },
    });

    const properties = geoJsonRectangle(result.shape);

    properties.anchor = anchor.value;
    properties.coordinate = coordinate.value;
    properties.geofenceBottom = geofenceBottom.value;
    properties.geofenceLeft = geofenceLeft.value;
    properties.geofenceRight = geofenceRight.value;
    properties.geofenceTop = geofenceTop.value;
    properties.heading = heading.value;
    properties.length = length.value;
    properties.width = width.value;

    const shapeId = editShapeId.value;
    if (shapeId) {
      setShapeId(result.shape, shapeId);
    }

    return result;
  });

  const allGeoJsonShapes = computed<ShapeGeoJson[]>(() => {
    const shapes: ShapeGeoJson[] = [];

    // Add the main shape if it exists
    if (geoJson.value) {
      shapes.push(geoJson.value);
    }

    // Add all other shapes in multi-edit mode with transformations applied
    if (multiEditMode.value && editShapeId.value) {
      const activeOriginal = originalShapeProperties.value.get(editShapeId.value);

      if (
        activeOriginal &&
        longitude.value !== undefined &&
        latitude.value !== undefined &&
        heading.value !== undefined
      ) {
        // Calculate deltas from the active shape
        const longitudeDelta = longitude.value - activeOriginal.coordinate[0];
        const latitudeDelta = latitude.value - activeOriginal.coordinate[1];
        const headingDelta = heading.value - activeOriginal.heading;

        for (const [shapeId, originalFeature] of editedShapes.value.entries()) {
          // Skip the currently active shape (already included above)
          if (shapeId === editShapeId.value) continue;

          const rectangle = geoJsonRectangle(originalFeature);
          const shapeOriginal = originalShapeProperties.value.get(shapeId);

          if (!shapeOriginal) continue;

          // Apply the deltas to this shape
          const result = createPreviewRectangle({
            anchor: rectangle.anchor ?? defaultAnchor,
            coordinate: {
              latitude: shapeOriginal.coordinate[1] + latitudeDelta,
              longitude: shapeOriginal.coordinate[0] + longitudeDelta,
            },
            heading: shapeOriginal.heading + headingDelta,
            width: shapeOriginal.width,
            length: shapeOriginal.length,
            geofence: {
              top: rectangle.geofenceTop ?? defaultGeofence,
              bottom: rectangle.geofenceBottom ?? defaultGeofence,
              left: rectangle.geofenceLeft ?? defaultGeofence,
              right: rectangle.geofenceRight ?? defaultGeofence,
            },
          });

          // Set properties on the shape to preserve geofence values
          const properties = geoJsonRectangle(result.shape);
          properties.anchor = rectangle.anchor ?? defaultAnchor;
          properties.coordinate = {
            longitude: shapeOriginal.coordinate[0] + longitudeDelta,
            latitude: shapeOriginal.coordinate[1] + latitudeDelta,
          };
          properties.geofenceBottom = rectangle.geofenceBottom ?? defaultGeofence;
          properties.geofenceLeft = rectangle.geofenceLeft ?? defaultGeofence;
          properties.geofenceRight = rectangle.geofenceRight ?? defaultGeofence;
          properties.geofenceTop = rectangle.geofenceTop ?? defaultGeofence;
          properties.heading = shapeOriginal.heading + headingDelta;
          properties.length = shapeOriginal.length;
          properties.width = shapeOriginal.width;

          setShapeId(result.shape, shapeId);
          shapes.push(result);
        }
      }
    }

    return shapes;
  });

  function rotate(value: number) {
    if (heading.value === undefined) return;
    heading.value += value;
  }

  function create(action: CreateShape) {
    stop();

    anchor.value = defaultAnchor;
    const coordinate = action.coordinate;
    longitude.value = coordinate.longitude;
    latitude.value = coordinate.latitude;
    heading.value = coordinate.heading ?? defaultHeading;
    width.value = action.width ?? defaultWidth;
    length.value = action.length ?? defaultLength;
    geofenceBottom.value = defaultGeofence;
    geofenceLeft.value = defaultGeofence;
    geofenceRight.value = defaultGeofence;
    geofenceTop.value = defaultGeofence;
    editShapeId.value = undefined;

    operation.value = 'create';
  }

  function edit(action: EditShape) {
    if (!action.addToSelection) {
      editedShapes.value.clear();
      originalShapeProperties.value.clear();
    }

    if (editShapeId.value && geoJson.value) {
      // When adding to selection, preserve the current state of the previously active shape
      const previousShapeId = editShapeId.value;
      editedShapes.value.set(previousShapeId, geoJson.value.shape);

      // Update the original properties to the current position
      // This becomes the new "original" reference point
      if (
        longitude.value !== undefined &&
        latitude.value !== undefined &&
        heading.value !== undefined &&
        width.value !== undefined &&
        length.value !== undefined
      ) {
        originalShapeProperties.value.set(previousShapeId, {
          coordinate: [longitude.value, latitude.value],
          heading: heading.value,
          width: width.value,
          length: length.value,
        });
      }
    }

    const shapeId = action.shapeId ?? getShapeId(action.feature);

    // Store the original feature
    editedShapes.value.set(shapeId, action.feature);

    // Store original properties for this shape using its OWN anchor
    const rectangle = geoJsonRectangle(action.feature);
    const shapeAnchor = rectangle.anchor ?? defaultAnchor;
    const previousAnchor = anchor.value;
    anchor.value = shapeAnchor; // Temporarily set to this shape's anchor
    const calculated = calculateProperties(action.feature);
    anchor.value = previousAnchor; // Restore previous anchor

    if (calculated) {
      originalShapeProperties.value.set(shapeId, {
        coordinate: [calculated.coordinate[0]!, calculated.coordinate[1]!],
        heading: calculated.heading,
        width: calculated.width,
        length: calculated.length,
      });
    }

    // Set current shape as active
    feature.value = action.feature;
    editShapeId.value = shapeId;
    fillValues(action.feature);
    operation.value = 'edit';
  }

  function copy(action: CopyShape) {
    stop();
    editShapeId.value = undefined;
    fillValues(action.feature, true);
    operation.value = 'create';
  }

  function fillValues(feature: GeoJSON.Feature, offsetStartPosition = false) {
    const rectangle = geoJsonRectangle(feature);

    anchor.value = rectangle.anchor ?? defaultAnchor;

    geofenceBottom.value = rectangle.geofenceBottom ?? defaultGeofence;
    geofenceLeft.value = rectangle.geofenceLeft ?? defaultGeofence;
    geofenceRight.value = rectangle.geofenceRight ?? defaultGeofence;
    geofenceTop.value = rectangle.geofenceTop ?? defaultGeofence;

    const properties = calculateProperties(feature);
    longitude.value = properties?.coordinate[0];
    latitude.value = properties?.coordinate[1];

    if (offsetStartPosition && longitude.value && latitude.value) {
      const offset = destination([longitude.value, latitude.value], 2, 45, turfOptions);

      longitude.value = offset.geometry.coordinates[0];
      latitude.value = offset.geometry.coordinates[1];
    }

    heading.value = properties?.heading ?? defaultHeading;
    width.value = properties?.width ?? defaultWidth;
    length.value = properties?.length ?? defaultLength;
  }

  function newCoordinate(coordinate: Coordinate) {
    longitude.value = coordinate.longitude;
    latitude.value = coordinate.latitude;
  }

  function remove(shapeId: string) {
    editedShapes.value.delete(shapeId);
    originalShapeProperties.value.delete(shapeId);

    if (editShapeId.value === shapeId) {
      // If we're removing the active shape and there are other shapes remaining,
      // make one of them the new active shape
      if (editedShapes.value.size > 0) {
        const remainingShapeId = Array.from(editedShapes.value.keys())[0]!;
        const remainingFeature = editedShapes.value.get(remainingShapeId);

        if (remainingFeature) {
          feature.value = remainingFeature;
          editShapeId.value = remainingShapeId;
          fillValues(remainingFeature);
        } else {
          editShapeId.value = undefined;
        }
      } else {
        editShapeId.value = undefined;
      }
    }
  }

  function stop() {
    operation.value = 'none';

    anchor.value = undefined;
    longitude.value = undefined;
    latitude.value = undefined;
    heading.value = undefined;
    width.value = undefined;
    length.value = undefined;
    geofenceTop.value = undefined;
    geofenceBottom.value = undefined;
    geofenceLeft.value = undefined;
    geofenceRight.value = undefined;
    editedShapes.value.clear();
    originalShapeProperties.value.clear();
  }

  function calculateProperties(feature?: GeoJSON.Feature) {
    if (feature === undefined) return;

    const geometry = feature.geometry;

    if (geometry.type === 'Polygon') {
      const p1 = geometry.coordinates[0]![0]!;
      const p2 = geometry.coordinates[0]![1]!;
      const p3 = geometry.coordinates[0]![2]!;
      const p4 = geometry.coordinates[0]![3]!;

      const heading = bearing(p1, p2);
      const length = distance(p1, p2, turfOptions);
      const width = distance(p2, p3, turfOptions);

      let coordinate: GeoJSON.Position = p1;

      switch (anchor.value) {
        case Anchor.Center:
          coordinate = midpoint(p1, p3).geometry.coordinates;
          break;
        case Anchor.BottomCenter:
          coordinate = midpoint(p4, p1).geometry.coordinates;
          break;
        case Anchor.BottomLeft:
          coordinate = p1;
          break;
        case Anchor.BottomRight:
          coordinate = p4;
          break;
        case Anchor.TopCenter:
          coordinate = midpoint(p2, p3).geometry.coordinates;
          break;
        case Anchor.TopLeft:
          coordinate = p2;
          break;
        case Anchor.TopRight:
          coordinate = p3;
          break;
      }

      return {
        coordinate,
        heading,
        length,
        width,
      };
    }
  }

  return {
    create,
    edit,
    copy,
    stop,
    rotate,
    newCoordinate,
    operation,
    anchor,
    longitude,
    latitude,
    heading,
    width,
    length,
    geofenceTop,
    geofenceBottom,
    geofenceLeft,
    geofenceRight,
    geoJson,
    allGeoJsonShapes,
    multiEditMode,
    coordinate,
    anchorChanged,
    longitudeChanged,
    latitudeChanged,
    headingChanged,
    widthChanged,
    lengthChanged,
    geofenceTopChanged,
    geofenceBottomChanged,
    geofenceLeftChanged,
    geofenceRightChanged,
    changed,
    rotationCoordinate,
    remove,
  };
});
